import { useParams } from "react-router";

import {
  type GetOrganisationBySlugQuery,
  type GetOrganisationQuery,
  useGetOrganisationBySlugQuery,
  useGetOrganisationQuery,
} from "generated/graphql";
import { useAuthOverride } from "shell/platform/hooks/useAuthOverride";

export type ResolvedOrganisation =
  | NonNullable<GetOrganisationQuery["organisation"]>
  | NonNullable<GetOrganisationBySlugQuery["organisationBySlug"]>;

export interface OrganizationAccess {
  error: Error | null;
  loading: boolean;
  organisationId?: string;
  organization: ResolvedOrganisation | null;
  refetch: () => Promise<unknown>;
  slug?: string;
}

const noopRefetch = async () => undefined;

export const useOrganizationAccess = (): OrganizationAccess => {
  const auth = useAuthOverride();
  const { organizationId } = useParams<{ organizationId?: string }>();

  const slug = auth?.user?.profile?.idp as string | undefined;
  const isAuthenticated = Boolean(auth?.isAuthenticated);

  const disabled = !isAuthenticated;

  const useById = Boolean(organizationId);
  const shouldSkipById = disabled || !organizationId;
  const shouldSkipBySlug = disabled || useById || !slug;

  const {
    data: dataById,
    loading: loadingById,
    error: errorById,
    refetch: refetchById,
  } = useGetOrganisationQuery({
    skip: shouldSkipById,
    variables: { organisationId: organizationId ?? "" },
    errorPolicy: "all",
  });

  const {
    data: dataBySlug,
    loading: loadingBySlug,
    error: errorBySlug,
    refetch: refetchBySlug,
  } = useGetOrganisationBySlugQuery({
    skip: shouldSkipBySlug,
    variables: { slug: slug ?? "" },
    errorPolicy: "all",
  });

  if (disabled) {
    return {
      loading: false,
      organization: null,
      organisationId: undefined,
      error: null,
      slug,
      refetch: noopRefetch,
    };
  }

  const resolvedOrganisation: ResolvedOrganisation | null = useById
    ? (dataById?.organisation ?? null)
    : (dataBySlug?.organisationBySlug ?? null);

  const loading = useById ? loadingById : loadingBySlug;
  const error = (useById ? errorById : errorBySlug) as Error | undefined;
  const refetch = useById ? refetchById : refetchBySlug;

  const canResolve = useById || Boolean(slug);
  const effectiveOrganisationId = useById
    ? organizationId
    : resolvedOrganisation?.organisationId;

  return {
    loading: canResolve ? loading : false,
    organization: canResolve ? resolvedOrganisation : null,
    organisationId: canResolve ? effectiveOrganisationId : undefined,
    error: canResolve ? (error ?? null) : null,
    slug,
    refetch: canResolve ? refetch : noopRefetch,
  };
};
